/**
 * fitxessBarcodeScanner — cross-platform JS wrapper around the app's barcode
 * scanners.
 *
 *   iOS      → custom native `FitxessBarcodeScanner` Capacitor plugin
 *              (AVFoundation-backed, Apple-clean). UNCHANGED.
 *   Android  → `@capacitor-mlkit/barcode-scanning` (Google ML Kit) using the
 *              ready-made Google code-scanner UI (`scan()`). The ML Kit iOS
 *              implementation is deliberately excluded from the iOS build via
 *              `ios.includePlugins` in capacitor.config.ts.
 *   web/etc. → returns null (caller falls back to manual UPC entry).
 *
 * Provider-agnostic: this module ONLY emits the UPC. Lookup against
 * FatSecret happens in `logFoodAPI.lookupBarcode(upc)`.
 *
 * Every failure mode (permission denied, module-install failure, cancel,
 * unsupported, unknown plugin error) resolves back to manual UPC entry with
 * NO crash and NO dead end.
 */
import { Capacitor, registerPlugin } from '@capacitor/core';
import { BarcodeScanner, BarcodeFormat } from '@capacitor-mlkit/barcode-scanning';

/** iOS custom plugin — mirrors the Swift methods. */
const FitxessBarcodeScanner = registerPlugin('FitxessBarcodeScanner');

/**
 * Retail food barcode formats only. Restricting the format set makes ML Kit
 * faster and avoids misreading QR / industrial symbologies as UPCs. Matches
 * what the FatSecret UPC lookup accepts.
 */
const RETAIL_FORMATS = [
  BarcodeFormat.UpcA,
  BarcodeFormat.UpcE,
  BarcodeFormat.Ean8,
  BarcodeFormat.Ean13,
];

/**
 * GoogleBarcodeScannerModuleInstallState values we care about.
 * (UNKNOWN=0, PENDING=1, DOWNLOADING=2, CANCELED=3, COMPLETED=4, FAILED=5, ...)
 */
const MODULE_STATE_COMPLETED = 4;
const MODULE_STATE_CANCELED = 3;
const MODULE_STATE_FAILED = 5;

// ─── iOS ─────────────────────────────────────────────────────────────────────

async function isIosScannerSupported() {
  try {
    const res = await FitxessBarcodeScanner.isSupported();
    const supported = !!(res && res.supported);
    console.log('[FitxessBarcodeScanner] isSupported() returned', JSON.stringify(res));
    return supported;
  } catch (err) {
    console.error('[FitxessBarcodeScanner] isSupported() threw — plugin likely not registered:', err && (err.message || err));
    return false;
  }
}

async function scanBarcodeIos() {
  if (!(await isIosScannerSupported())) {
    return null;
  }
  const res = await FitxessBarcodeScanner.scan();
  // res shape: { upc, format, cancelled } from Swift CAPPluginCall.resolve(...).
  return res || null;
}

// ─── Android (ML Kit) ─────────────────────────────────────────────────────────

/**
 * Ensures the on-demand Google code-scanner module is installed. Resolves
 * true on success, false if the install was cancelled/failed/timed out.
 * Never throws.
 */
function ensureGoogleModule() {
  return new Promise((resolve) => {
    let settled = false;
    let listener = null;
    let timer = null;
    const finish = (val) => {
      if (settled) return;
      settled = true;
      if (timer) clearTimeout(timer);
      if (listener && listener.remove) { try { listener.remove(); } catch (e) { /* noop */ } }
      resolve(val);
    };

    (async () => {
      try {
        listener = await BarcodeScanner.addListener(
          'googleBarcodeScannerModuleInstallProgress',
          (event) => {
            const state = event && event.state;
            if (state === MODULE_STATE_COMPLETED) finish(true);
            else if (state === MODULE_STATE_CANCELED || state === MODULE_STATE_FAILED) finish(false);
          }
        );
        await BarcodeScanner.installGoogleBarcodeScannerModule();
        // Completion is signalled via the event above; guard against a
        // progress event that never arrives.
        timer = setTimeout(() => finish(false), 60000);
      } catch (err) {
        console.error('[BarcodeScanner][android] installGoogleBarcodeScannerModule failed:', err && (err.message || err));
        finish(false);
      }
    })();
  });
}

async function scanBarcodeAndroid() {
  // 1) Make sure the Google code-scanner module is present.
  try {
    const res = await BarcodeScanner.isGoogleBarcodeScannerModuleAvailable();
    if (!res || !res.available) {
      const installed = await ensureGoogleModule();
      if (!installed) {
        console.warn('[BarcodeScanner][android] scanner module unavailable — falling back to manual entry');
        return null; // caller → manual UPC entry
      }
    }
  } catch (err) {
    console.error('[BarcodeScanner][android] module availability check failed:', err && (err.message || err));
    return null; // graceful fallback
  }

  // 2) Present the ready-made Google code scanner (retail formats only).
  try {
    const { barcodes } = await BarcodeScanner.scan({ formats: RETAIL_FORMATS });
    if (!barcodes || barcodes.length === 0) {
      return { cancelled: true };
    }
    const b = barcodes[0];
    const upc = ((b && (b.rawValue || b.displayValue)) || '').trim();
    if (!upc) return { cancelled: true };
    return { upc, format: b.format };
  } catch (err) {
    const msg = String((err && (err.message || err.code || err)) || '');
    // Google code scanner rejects when the user backs out.
    if (/cancel/i.test(msg)) return { cancelled: true };
    if (/permission/i.test(msg)) {
      const e = new Error('PERMISSION_DENIED');
      e.code = 'PERMISSION_DENIED';
      throw e;
    }
    console.error('[BarcodeScanner][android] scan failed:', msg);
    return null; // never a dead end — fall back to manual entry
  }
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Returns true when a native scanner can run on this platform
 * (iOS custom plugin, or Android ML Kit). Web/other → false.
 */
export async function isBarcodeScannerSupported() {
  if (!Capacitor.isNativePlatform()) {
    console.log('[BarcodeScanner] not supported: web platform');
    return false;
  }
  const platform = Capacitor.getPlatform();
  if (platform === 'ios') {
    return await isIosScannerSupported();
  }
  if (platform === 'android') {
    // ML Kit ships with Google Play Services on modern Android; the code
    // scanner module is fetched on demand at scan time.
    return true;
  }
  console.log('[BarcodeScanner] not supported: platform=' + platform);
  return false;
}

/**
 * Present the scanner. Resolves with:
 *   { upc: string, format: string }   on successful scan
 *   { cancelled: true }                if the user dismissed without scanning
 *   null                                if not supported / gracefully unavailable
 *
 * Rejects only on a hard permission denial (code 'PERMISSION_DENIED') so the
 * caller can prompt the user to enable the camera. All other errors resolve
 * to null so the UI falls back to manual UPC entry.
 */
export async function scanBarcode() {
  if (!Capacitor.isNativePlatform()) {
    return null;
  }
  const platform = Capacitor.getPlatform();
  if (platform === 'ios') {
    return await scanBarcodeIos();
  }
  if (platform === 'android') {
    return await scanBarcodeAndroid();
  }
  return null;
}

export default { scanBarcode, isBarcodeScannerSupported };
